import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, Logger } from '@saviynt/common';
import { Checkbox } from '@saviynt/design-system';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
  getCertDetailsData,
  resetDynamicSortDetails,
  setIsBulkActionsCompleted,
  setPersonalizationStatus,
  setSidePanelVisible,
  updateCertsFilter,
} from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  GROUP_BY_APP_VALUE,
  ITEMS_API_OFFSET,
} from '../../../utils/constant';
import { getMessageKey } from '../../../utils/utils';
import { CERT_ENTITLEMENT_VALUEKEY } from '../DynamicSort/constants';

import {
  EO_UPDATE_ROW_FILTER_MAPPING,
  UPDATE_ROW_FILTER_MAPPING,
} from './constant';
import { assignCountToRowFilterTree } from './utils';

function RowsFilter({
  filters,
  appliedRowFilters,
  setAppliedRowFilters,
  level,
}) {
  const dispatch = useDispatch();
  const intl = useIntl();

  const [isEntireFilterDisabled, setIsEntireFilterDisabled] = useState(false);
  // Set initial state, only needed for the Menu open transition
  const updatedFiltersWithCount = assignCountToRowFilterTree(
    filters,
    appliedRowFilters
  );
  const [nestedFilters, setNestedFilters] = useState(updatedFiltersWithCount);

  // TODO: Use redux rowFilters instead of calling assignCountToRowFilterTree twice every mount
  const {
    core: { columnNames, searchText, campaignType },
    global: { certKey },
    viewControls: { groupByOptions },
  } = useSelector((state) => state.certifications?.details);

  const { messages } = useSelector((state) => state?.user);

  const selectedGroupByValue = groupByOptions?.find(
    (option) => option.isChecked
  )?.value;
  const isUsingGroupBy = selectedGroupByValue === GROUP_BY_APP_VALUE;
  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;

  const TYPE_TITLES = {
    Role:
      messages['Role_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('Role_DISPLAYNAME.CERTIFICATIONS')),
    Account:
      messages['Account_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('Account_DISPLAYNAME.CERTIFICATIONS')),
    User:
      messages['User_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('User_DISPLAYNAME.CERTIFICATIONS')),
    Entitlement:
      messages['Ent_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('Ent_DISPLAYNAME.CERTIFICATIONS')),
    'Child Entitlement':
      messages['ChildEnt_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('ChildEnt_DISPLAYNAME.CERTIFICATIONS')),
  };

  // Sync applied filters isChecked on mount
  useEffect(() => {
    setNestedFilters(updatedFiltersWithCount);
  }, []);

  const handleToggle = (filterValue) => {
    const rowsFilter = isEOCampaign
      ? EO_UPDATE_ROW_FILTER_MAPPING
      : UPDATE_ROW_FILTER_MAPPING;
    // EO campaign fieldname is ROW_TYPE_NAME
    const rowFieldName = isEOCampaign ? 'ROW_TYPE_NAME' : 'ROW_TYPE';

    dispatch(resetDynamicSortDetails());

    if (appliedRowFilters.includes(filterValue)) {
      if (appliedRowFilters.length === 1) {
        Logger.warn('Atleast one filter is required');

        return;
      }

      setIsEntireFilterDisabled(true);
      dispatch(setSidePanelVisible(false));

      dispatch(
        updateCertsFilter(rowFieldName, rowsFilter?.[filterValue], false)
      ).then((response) => {
        if (response) {
          dispatch(
            getCertDetailsData(
              certKey,
              columnNames,
              ITEMS_API_OFFSET,
              0,
              searchText
            )
          );

          setAppliedRowFilters((prev) =>
            prev.filter((item) => item !== filterValue)
          );
          setIsEntireFilterDisabled(false);
        }
      });
    } else {
      const newAppliedFilters = [...appliedRowFilters, filterValue];

      setIsEntireFilterDisabled(true);
      dispatch(setSidePanelVisible(false));

      dispatch(
        updateCertsFilter(rowFieldName, rowsFilter?.[filterValue], true)
      ).then((response) => {
        if (response) {
          dispatch(
            getCertDetailsData(
              certKey,
              columnNames,
              ITEMS_API_OFFSET,
              0,
              searchText
            )
          );
          setAppliedRowFilters(newAppliedFilters);
          setIsEntireFilterDisabled(false);
        }
      });
    }

    setNestedFilters((prev) =>
      prev.map((filter) => {
        if (filter.value === filterValue) {
          return {
            ...filter,
            isChecked: !filter.isChecked,
          };
        }

        return filter;
      })
    );

    dispatch(setPersonalizationStatus('UPDATE_PERSONALIZATION'));
    dispatch(setIsBulkActionsCompleted(false));
  };

  useEffect(() => {
    if (appliedRowFilters?.length === 1) {
      setNestedFilters((prev) =>
        prev.map((filter) => ({
          ...filter,
          isLast: filter.value === appliedRowFilters[0],
        }))
      );
    }

    if (appliedRowFilters?.length > 1) {
      setNestedFilters((prev) =>
        prev.map((filter) => ({
          ...filter,
          isLast: false,
        }))
      );
    }
  }, [appliedRowFilters]);

  // disable selection for rows when group by applied
  const disableSelectionRowForGroup =
    appliedRowFilters.length === 2 &&
    isUsingGroupBy &&
    appliedRowFilters.includes(CERT_ENTITLEMENT_VALUEKEY.USER);

  return (
    <div className='RowsFilter-filterList'>
      {nestedFilters?.map((filter) => (
        <React.Fragment key={filter?.value}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className='RowsFilter-filterListItem' key={filter?.value}>
            <div
              className={classNames(
                'RowsFilter-filterListItemWrapper',
                filter?.label === 'User' &&
                  isUsingGroupBy &&
                  'RowsFilter-item--isUsingGroupBy'
              )}
              style={{
                transform: `translateX(-${level * 1.125}rem)`,
                width: `calc(100% + ${level * 1.125 + 0.875}rem)`,
              }}
            />
            {!filter?.isParent && (
              <div
                className='RowsFilter-filterListItemConnectors--top'
                style={{ height: `${(filter.count + 1) * 2.375}rem` }}
              />
            )}
            <div className='RowsFilter-filterListItemCheckboxParent'>
              {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
              <div
                className='RowsFilter-filterListItemCheckboxChild'
                role='button'
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isEntireFilterDisabled) {
                    handleToggle(filter?.value);
                  }
                }}>
                <Checkbox
                  id='rows-filter-checkbox'
                  name={filter?.value}
                  isChecked={
                    (isUsingGroupBy && filter?.label === 'User') ||
                    filter?.isChecked ||
                    filter?.isDisabled
                  }
                  onChange={() => {
                    if (!isEntireFilterDisabled) handleToggle(filter?.value);
                  }}
                  isDisabled={
                    (filter?.label === 'User' && isUsingGroupBy) ||
                    filter?.isDisabled ||
                    filter?.isLast ||
                    isEntireFilterDisabled ||
                    (disableSelectionRowForGroup && filter?.isChecked)
                  }
                  dataTestId='checkbox-no-hovereffect'
                />
              </div>
            </div>
            <p className='RowsFilter-filterListItemTextBox'>
              {TYPE_TITLES[filter?.label]}
            </p>
          </label>
          {filter?.options.length > 0 && (
            <RowsFilter
              filters={filter?.options}
              appliedRowFilters={appliedRowFilters}
              setAppliedRowFilters={setAppliedRowFilters}
              isEntireFilterDisabled={isEntireFilterDisabled}
              setIsEntireFilterDisabled={setIsEntireFilterDisabled}
              level={level + 1}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

RowsFilter.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      isChecked: PropTypes.bool.isRequired,
      isParent: PropTypes.bool,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.string,
          isChecked: PropTypes.bool.isRequired,
        })
      ),
    })
  ).isRequired,
  appliedRowFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  setAppliedRowFilters: PropTypes.func.isRequired,
  level: PropTypes.number,
};

RowsFilter.defaultProps = { level: 0 };

export default RowsFilter;
